import React, { useState } from "react";
//#region types
import { IconDefinitionConstants } from "utils/constants";
import { BaseInputProps } from "models/propTypes/wintFormPropsTypes";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { CircularProgress, Grid, InputAdornment, TextField } from "@mui/material";
import Icon from "../../icon";
import StyledButton from "../../buttons/styledButton";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { LocalizationHelperInstance, UtilsHelper } from "utils/helpers";

function BaseInput(props: React.PropsWithChildren<BaseInputProps>) {
  const [hasFocus, setHasFocus] = useState(false);
  const variant = props.label ? "filled" : "outlined";

  const onCopyToClipBoard = () => {
    navigator.clipboard.writeText(props.value);
  };

  const onFocus = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    props.onFocus && props.onFocus(event);
    setHasFocus(true);
  };

  const onBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    props.onBlur && props.onBlur(event);
    setHasFocus(false);
  };

  const shrinkLabel = hasFocus || !UtilsHelper.isEmpty(props.value);

  return (
    <span className={clsx(cssExports.iconInputContainer, props.rootClass, props.className)}>
      <TextField
        fullWidth
        select={props.select}
        minRows={props.minRows ?? undefined}
        maxRows={props.maxRows ?? undefined}
        InputProps={{
          value: props.value,
          name: props.name,
          type: props.type,
          error: props.hasError,
          multiline: props.multiline,
          rows: !props.minRows && props.multiline ? (props.rows ?? 5) : undefined,
          disabled: props.disabled,
          readOnly: props.readonly,
          ...(props.InputProps ?? {}),
          startAdornment: props.icon && !props.endIcon && (
            <InputAdornment position="start" className={cssExports.inputAdornedStart}>
              <Icon icon={props.icon} className={cssExports.inputIcon} />
            </InputAdornment>
          ),
          endAdornment: (
            <>
              {props.endAdornment}
              {props.InputProps?.endAdornment}

              {props.showCopyButton && (
                <InputAdornment position="end" className={cssExports.inputAdornedStart}>
                  <StyledButton
                    size={"xs"}
                    className={clsx(cssExports.iconButton, cssExports.transparent)}
                    onClick={onCopyToClipBoard}
                  >
                    <Icon icon={IconDefinitionConstants.WCopy} />
                  </StyledButton>
                </InputAdornment>
              )}

              {props.icon && props.endIcon && (
                <InputAdornment position="end" className={cssExports.inputAdornedStart}>
                  <Icon icon={props.icon} className={cssExports.inputIcon} />
                </InputAdornment>
              )}

              {props.isLoading && (
                <InputAdornment position="end" className={cssExports.inputAdornedEnd}>
                  <CircularProgress size={20} className={cssExports.inputProgress} />
                </InputAdornment>
              )}

              {props.disabled ? (
                <InputAdornment position="end" className={clsx(cssExports.inputAdornedEnd, cssExports.disabledIcon)}>
                  <Icon icon={IconDefinitionConstants.WLock} className={cssExports.inputIcon} />
                </InputAdornment>
              ) : props.success ? (
                <InputAdornment position="end" className={clsx(cssExports.inputAdornedEnd, cssExports.success)}>
                  <Icon icon={IconDefinitionConstants.WCheck} className={cssExports.inputIcon} />
                </InputAdornment>
              ) : props.hasError ? (
                <InputAdornment position="end" className={clsx(cssExports.inputAdornedEnd, cssExports.error)}>
                  <Icon icon={IconDefinitionConstants.WExclamation} className={cssExports.inputIcon} />
                </InputAdornment>
              ) : (
                <></>
              )}
            </>
          ),
          classes: {
            root: clsx(
              cssExports.inputRoot,
              props.icon ? cssExports.iconInput : "",
              props.multiline ? cssExports.inputMultilineRoot : "",
              props.readonly && props.readonlyStyle === "disabled"
                ? cssExports.disabledInput
                : props.readonly
                  ? cssExports.readOnlyInput
                  : "",
              shrinkLabel && props.label ? cssExports.shrinkedLabelInput : "",
              props.rightAlign && cssExports.inputRight
            ),
            input: cssExports.inputInput,
            focused: cssExports.focusedInput,
            error: cssExports.errorInput,
            disabled: cssExports.disabledInput,
            notchedOutline: cssExports.notchedOutline,
          },
          onChange: props.onChange,
          onBlur: onBlur,
          onFocus: onFocus,
          onClick: props.onClick,
        }}
        InputLabelProps={{
          shrink: shrinkLabel,
          classes: {
            root: clsx(
              cssExports.inputLabel,
              props.icon ? cssExports.iconInputLabel : "",
              props.readonly && props.readonlyStyle !== "disabled" ? cssExports.readOnlyLabel : "",
              props.rightAlign && cssExports.labelRight
            ),
            error: cssExports.errorLabel,
            shrink: cssExports.shrinkedLabel,
            focused: cssExports.focusedLabel,
          },
        }}
        inputProps={{
          ...(props.inputProps ?? {}),
          maxLength: props.format === undefined ? props.maxLength : undefined,
          style: { textAlign: props.rightAlign ? "right" : "left" },
          "area-autocomplete": props.browserAutocompleteOff ? "no" : undefined,
        }}
        FormHelperTextProps={{
          classes: {
            root: cssExports.inputHelperText,
            error: cssExports.errorHelperText,
          },
        }}
        label={
          props.rightAlign ? (
            <Grid container justifyContent="flex-end" className={props.disabled ? cssExports.disableLabel : ""}>
              {props.label}
            </Grid>
          ) : shrinkLabel ? (
            props.label
          ) : (
            `${props.placeholder ?? props.label}${
              props.optional
                ? `${props.placeholder || props.label ? " " : ""}(${LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.Common.Optional
                  )})`
                : ""
            }`
          )
        }
        error={props.hasError}
        variant={variant as any}
        data-testid={`${props.name}BaseInput`}
      >
        {props.children}
      </TextField>
    </span>
  );
}

export default BaseInput;
